import React, { useState, useEffect } from 'react';
import { ArrowRight, Building2, Save } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../contexts/auth/hooks';
import { supabase } from '../../../lib/supabase';
import { BusinessLogo } from './components/BusinessLogo';
import { ContactInfo } from './components/ContactInfo';
import { BookingLink } from './components/BookingLink';
import { UnsavedChangesIndicator } from '../../../components/ui/UnsavedChangesIndicator';
import { SkeletonLoader } from '../../../components/ui/SkeletonLoader';
import { debounce } from '../../../utils/debounce';
import { retryWithBackoff } from '../../../utils/errorRecovery';
import { useOfflineMode } from '../../../hooks/useOfflineMode';
import toast from 'react-hot-toast';

const BUCKET = 'business_assets';

export default function BusinessSettings() {
  const { user, loading: authLoading } = useAuth();
  const [businessData, setBusinessData] = useState<any>(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pendingChanges, setPendingChanges] = useState<{
    name?: string;
    logo_url?: string | null;
    booking_link?: string;
    contact_info?: any;
  }>({});
  const { isOffline } = useOfflineMode();

  // Simplified logo migration
  async function migrateLogoFromRegistrationDraft(businessId: string) {
    try {
      const draftRaw = localStorage.getItem('registration_draft');
      if (!draftRaw) return false;

      let draftPath: string | undefined;
      let draftUrl: string | undefined;

      try {
        const parsed = JSON.parse(draftRaw);
        draftPath = parsed?.step2?.logo_path;
        draftUrl = parsed?.step2?.logo_url;
      } catch (e) {
        return false;
      }

      // Copy logo from draft if exists
      if (draftPath) {
        const ext = draftPath.split('.').pop() || 'png';
        const destPath = `${businessId}/logo-${Date.now()}.${ext}`;

        const { error: copyErr } = await supabase.storage.from(BUCKET).copy(draftPath, destPath);
        if (copyErr) throw copyErr;

        await supabase.storage.from(BUCKET).remove([draftPath]);

        const { error: upErr } = await supabase.from('businesses').update({ logo_url: destPath }).eq('id', businessId);
        if (upErr) throw upErr;

        // Clear draft
        try {
          const parsed = JSON.parse(draftRaw);
          if (parsed?.step2) {
            delete parsed.step2.logo_path;
            delete parsed.step2.logo_url;
          }
          localStorage.setItem('registration_draft', JSON.stringify(parsed));
        } catch {}

        return true;
      }

      // Use URL directly if no path
      if (draftUrl) {
        const { error: upErr } = await supabase.from('businesses').update({ logo_url: draftUrl }).eq('id', businessId);
        if (upErr) throw upErr;
        return true;
      }

      return false;
    } catch (e) {

      return false;
    }
  }
  // -------------------------------------------------------------------------------------------------

  const loadBusinessData = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('business_id')
        .eq('id', user.id)
        .single();
      if (userError) throw userError;
      if (!userData?.business_id) throw new Error('לא נמצא עסק מקושר');

      let { data: biz, error: businessError } = await supabase
        .from('businesses')
        .select('*')
        .eq('id', userData.business_id)
        .single();
      if (businessError) throw businessError;

      // אם אין לוגו – נסה להגר מההרשמה
      if (!biz.logo_url) {
        const migrated = await migrateLogoFromRegistrationDraft(userData.business_id);
        if (migrated) {
          const refreshed = await supabase
            .from('businesses')
            .select('*')
            .eq('id', userData.business_id)
            .single();
          if (!refreshed.error && refreshed.data) {
            biz = refreshed.data;
          }
        }
      }

      setBusinessData(biz);
      setPendingChanges({});
      setHasChanges(false);
    } catch (error) {

      toast.error('שגיאה בטעינת נתוני העסק');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBusinessData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  const handleNameChange = (name: string) => {
    setPendingChanges(prev => ({ ...prev, name }));
    setHasChanges(true);
  };

  const handleLogoChange = async (logoPathOrUrl: string) => {
    setPendingChanges(prev => ({ ...prev, logo_url: logoPathOrUrl }));
    setHasChanges(true);

    if (!businessData?.id) {
      toast.error('לא נמצא מזהה עסק');
      return;
    }
    const { error } = await supabase
      .from('businesses')
      .update({ logo_url: logoPathOrUrl })
      .eq('id', businessData.id);
    if (error) {

      toast.error('שגיאה בשמירת הלוגו');
      return;
    }
    await loadBusinessData();
    toast.success('הלוגו נשמר');
  };

  const handleBookingLinkChange = (bookingLink: string) => {
    setPendingChanges(prev => ({ ...prev, booking_link: bookingLink }));
    setHasChanges(true);
  };

  const handleContactInfoChange = (contactInfo: any) => {
    setPendingChanges(prev => ({ ...prev, contact_info: contactInfo }));
    setHasChanges(true);
  };

  const handleSave = async () => {
    if (!businessData?.id || !hasChanges || saving) return;

    if (isOffline) {
      toast.error('אין חיבור לאינטרנט. אנא בדוק את החיבור ונסה שוב');
      return;
    }

    try {
      setSaving(true);

      // Save with retry logic
      await retryWithBackoff(async () => {
        const { error } = await supabase
          .from('businesses')
          .update({ ...pendingChanges })
          .eq('id', businessData.id);
        if (error) throw error;
      }, {
        maxRetries: 3,
        onRetry: (attempt) => {
          toast(`מנסה שוב (${attempt}/3)...`, { icon: '🔄' });
        }
      });

      toast.success('הפרטים נשמרו בהצלחה');
      await loadBusinessData();
    } catch (error: any) {

      toast.error(error.message || 'שגיאה בשמירת הפרטים', {
        duration: 5000,
        action: {
          label: 'נסה שוב',
          onClick: () => handleSave()
        }
      } as any);
      await loadBusinessData();
    } finally {
      setSaving(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="h-full flex flex-col min-h-0 bg-gray-50">
        <div className="flex-1 overflow-y-auto pb-24">
          <div className="sticky top-0 z-10 bg-gray-50 px-4 pt-4 pb-2">
            <div className="flex items-center justify-between">
              <div className="h-8 w-32 bg-gray-200 rounded animate-pulse" />
            </div>
          </div>
          <div className="space-y-4 px-4 pt-2">
            <SkeletonLoader type="form" rows={3} />
          </div>
        </div>
      </div>
    );
  }

  if (!businessData) {
    return (
      <div className="min-h-[400px] flex flex-col items-center justify-center gap-4">
        <p className="text-red-500">לא נמצאו נתוני עסק</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700"
        >
          טען מחדש
        </button>
      </div>
    );
  }

  const displayData = { ...businessData, ...pendingChanges };

  return (
    <div className="h-full flex flex-col min-h-0 bg-gray-50">
      <div className="flex-1 overflow-y-auto pb-24">
        <div className="sticky top-0 z-10 bg-gray-50 px-4 pt-4 pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/settings" className="text-gray-500 active:text-gray-700">
                <ArrowRight className="h-6 w-6" />
              </Link>
              <h1 className="text-2xl font-bold text-gray-900">הגדרות עסק</h1>
            </div>
            {hasChanges && (
              <button
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg active:bg-blue-700 transition-colors disabled:opacity-50"
              >
                <Save className="h-4 w-4" />
                <span className="text-sm font-medium">{saving ? 'שומר...' : 'שמור'}</span>
              </button>
            )}
          </div>
        </div>

        <div className="space-y-4 px-4 pt-2">
          <div className="bg-white rounded-xl overflow-hidden shadow-sm">
            <div className="px-4 py-3 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-900">פרטי עסק</h2>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1.5">
                  שם העסק
                </label>
                <input
                  type="text"
                  value={displayData.name}
                  onChange={(e) => {
                    setHasChanges(true);
                    setPendingChanges(prev => ({ ...prev, name: e.target.value }));
                  }}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                  placeholder="הזן את שם העסק"
                />
              </div>

              <BusinessLogo
                business={displayData}
                onLogoChange={handleLogoChange}
              />
            </div>
          </div>

          <div className="bg-white rounded-xl overflow-hidden shadow-sm">
            <div className="px-4 py-3 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-900">פרטי קשר</h2>
            </div>
            <div className="p-4">
              <ContactInfo
                contactInfo={displayData.contact_info}
                onChange={(ci) => {
                  setHasChanges(true);
                  setPendingChanges(prev => ({ ...prev, contact_info: ci }));
                }}
              />
            </div>
          </div>

          <div className="bg-white rounded-xl overflow-hidden shadow-sm">
            <div className="px-4 py-3 border-b border-gray-100">
              <h2 className="text-sm font-semibold text-gray-900">קישור הזמנה</h2>
            </div>
            <div className="p-4">
              <BookingLink
                bookingLink={displayData.booking_link}
                businessId={displayData.id}
                onBookingLinkChange={(val) => {
                  setHasChanges(true);
                  setPendingChanges(prev => ({ ...prev, booking_link: val }));
                }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Offline Indicator */}
      {isOffline && (
        <div className="fixed top-0 left-0 right-0 bg-red-500 text-white text-center py-2 text-sm z-50">
          אין חיבור לאינטרנט. השינויים יישמרו כשהחיבור יחזור
        </div>
      )}

      {/* Unsaved Changes Indicator */}
      <UnsavedChangesIndicator
        show={hasChanges && !saving}
        onSave={handleSave}
        onDiscard={() => {
          setPendingChanges({});
          setHasChanges(false);
          loadBusinessData();
        }}
        saving={saving}
      />
    </div>
  );
}
